import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, HStack } from '@televet/kibble-ui/build/chakra';
import WildCardButton, { WildCardButtonProps } from './WildCardButton';
import WildcardInput, { WildcardInputProps } from './WildcardInput';
import { WildcardValues } from 'shared/enums/WildcardOptions';
import WildcardTimeFormatSelect, { WildcardTimeFormatSelectProps } from './WildcardTimeFormatSelect';

export enum WildcardInputTypes {
  'TextInput',
  'TextArea',
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Dict<T = any> = Record<string, T>;

interface InputProps
  extends Omit<
    WildcardInputProps,
    'isInputFocused' | 'setIsInputFocused' | 'setIsMenuFocused' | 'showPreview' | 'availableWildCards'
  > {
  allowFillInTheBlanks?: boolean;
  validate?: (inputValue: string) => void;
}

// eslint-disable-next-line
interface ButtonProps extends Pick<WildCardButtonProps, 'size'> {}

interface WildCardInputGroupProps {
  buttonProps?: ButtonProps;
  inputProps: InputProps;
  shouldShowTimeFormatting?: boolean;
  timeFormatProps?: WildcardTimeFormatSelectProps;
  containerProps?: Dict;
  availableWildCards: WildcardValues[];
}

export const WildCardInputGroup = ({
  buttonProps,
  inputProps,
  shouldShowTimeFormatting = true,
  timeFormatProps,
  containerProps,
  availableWildCards,
}: WildCardInputGroupProps): JSX.Element => {
  const [showPreview, setShowPreview] = useState<boolean>(true);
  const [isMenuFocused, setIsMenuFocused] = useState<boolean>(false);
  const [isInputFocused, setIsInputFocused] = useState<boolean>(false);
  const textInputRef = useRef<HTMLInputElement | null>(null);
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const { onInputValueChange, inputType } = inputProps;

  const isTimeFormattingVisible = useMemo(() => {
    return inputProps?.inputValue?.includes(WildcardValues.Time) && availableWildCards.includes(WildcardValues.Time);
  }, [inputProps, availableWildCards]);

  useEffect(() => {
    setShowPreview(!isMenuFocused && !isInputFocused);
  }, [isMenuFocused, isInputFocused]);

  return (
    <Box {...containerProps}>
      <WildcardInput
        setIsInputFocused={setIsInputFocused}
        setIsMenuFocused={setIsMenuFocused}
        isInputFocused={isInputFocused}
        showPreview={showPreview}
        textInputRef={textInputRef}
        textAreaRef={textAreaRef}
        availableWildCards={availableWildCards}
        {...inputProps}
      />
      {/* TODO: Add error handling */}
      <HStack className="Automations__CustomNotification-Controls" width="100%" justify="space-between" align="center">
        <WildCardButton
          inputRef={inputType === WildcardInputTypes.TextArea ? textAreaRef : textInputRef}
          setIsMenuFocused={setIsMenuFocused}
          setIsInputFocused={setIsInputFocused}
          onInputValueChange={onInputValueChange}
          availableWildCards={availableWildCards}
          {...buttonProps}
        />
        {shouldShowTimeFormatting && isTimeFormattingVisible && <WildcardTimeFormatSelect {...timeFormatProps} />}
      </HStack>
    </Box>
  );
};
